import Button from '@jbhi-fi/lanyard-ui/components/Button';
import AutoComplete from '@jbhi-fi/lanyard-ui/components/AutoComplete';
import React, { useState, ChangeEvent } from 'react';
import { styles } from './LocationSearch.css';
import CoordinatesIcon from '@jbhi-fi/lanyard-ui/components/Icons/Coordinates';
import { DeliveryLocation } from '../localisation/utils/types';
import { AlgoliaApiClient } from '../../../api/localisation/AlgoliaApiClient';
import LocationSearchResults from './LocationSearchResults';
import Spinner from '@jbhi-fi/lanyard-ui/components/Spinner/Spinner';
import { useTracking, trackingContext } from '@jbhi-fi/components/src/Tracking';
import { useLocationSearch } from './useLocationSearch';

export type LocationSearchProps = {
  id: string;
  name?: string;
  variant?: 'default' | 'inline';
  initialValue?: string;
  showUseMyLocationButton?: boolean;
  showUseMyLocationLink?: boolean;
  inputError?: string;
  hasInputError?: boolean;
  gtmEventSource?: string;
  onLocationSelect?: (value: DeliveryLocation) => void;
  onClearInput?: () => void;
  validateClickAndCollectStore?: () => void;
};

export type UserLocation = {
  userPermission: boolean;
  coordinates: {
    lat: number;
    lng: number;
  };
};

const LocationSearch = ({
  id,
  name,
  variant = 'default',
  initialValue,
  showUseMyLocationButton = true,
  showUseMyLocationLink = false,
  inputError,
  hasInputError,
  gtmEventSource,
  onLocationSelect,
  onClearInput,
}: LocationSearchProps) => {
  const [searchTerm, setSearchTerm] = useState<string | undefined>();
  const { tracker } = useTracking({
    context: trackingContext.localisation,
    category: 'localisation',
    group: 'location-search',
  });
  const { fetchAndSelectUserLocation } = useLocationSearch(onLocationSelect);

  const handleSearchSelected = () => {
    tracker.trackClick({
      label: 'location-search-focussed',
      source: gtmEventSource,
    });
  };

  const handleResultSelected = (result: DeliveryLocation) => {
    onLocationSelect && onLocationSelect(result);
    tracker.trackSearch({ label: 'location-selected', source: gtmEventSource, extraData: { value: result?.postcode } });
  };

  const handleClearInput = () => {
    if (onClearInput) onClearInput();
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    if (inputError) {
      onClearInput?.();
    }
  };

  const handleUseMyLocationClick = async () => {
    const result = await fetchAndSelectUserLocation();
    setSearchTerm(result?.displayName ?? '');
    tracker.trackClick({
      label: 'use-my-location',
      source: gtmEventSource,
    });
    tracker.trackSearch({ label: 'location-selected', source: gtmEventSource, extraData: { value: result?.postcode } });
  };

  return (
    <div className={`${styles.locationSearch} ${variant}`} data-testid="location-search">
      <AutoComplete
        id={id}
        name={name || id}
        className={styles.inputContainer}
        value={searchTerm ?? initialValue}
        onFocus={handleSearchSelected}
        label="Enter postcode or suburb"
        minNumOfChars={2}
        errorMsg={inputError}
        hasError={hasInputError}
        onChange={handleOnChange}
        onClearInput={handleClearInput}
        isPastingEnabled={true}
        getApiPromise={() => {
          if (!searchTerm || searchTerm?.length <= 2) {
            return null;
          } else {
            return AlgoliaApiClient.getLocations({ body: searchTerm }) as Promise<any>;
          }
        }}
        renderSuggestions={({
          data,
          isLoading,
          errorMsg,
          setInputValue,
          setData,
        }: {
          data: Array<DeliveryLocation>;
          isLoading: boolean;
          errorMsg: string;
          setInputValue: (value: string) => void;
          setData: (value: Array<unknown>) => void;
        }) => {
          if (!searchTerm || searchTerm?.length <= 2) {
            return null;
          }
          if (isLoading) {
            return (
              <div className={styles.loader} data-testid="location-search-loader">
                <Spinner />
              </div>
            );
          }
          if (errorMsg || inputError) {
            return (
              <div className={styles.errorContainer} data-testid="location-search-no-results">
                {inputError ?? errorMsg}
              </div>
            );
          }
          if (!data || !data.length) {
            return null;
          }
          return (
            <LocationSearchResults
              data={data}
              setInputValue={setInputValue}
              onSelect={handleResultSelected}
              setData={setData}
            />
          );
        }}
      />
      {showUseMyLocationButton && !showUseMyLocationLink ? (
        <>
          <div className={`${styles.seperatorBlock} ${variant}`}>or</div>
          <div className={`${styles.useLocationButtonContainer} ${variant}`}>
            <Button
              type="secondary"
              classes={styles.useLocationButton}
              onClick={handleUseMyLocationClick}
              iconPosition="left"
              icon={<CoordinatesIcon />}
              label="Use current location"
              data-testid="location-search-use-location-button"
            />
          </div>
        </>
      ) : null}
      {showUseMyLocationLink ? (
        <>
          <div className={styles.useLocationLinkContainer}>
            <Button type="link" onClick={handleUseMyLocationClick} label="Use my location" />
          </div>
        </>
      ) : null}
    </div>
  );
};

export default LocationSearch;
